import { useState, useEffect } from 'react'
import { X } from 'lucide-react'
import api, { Campaign, Organisation, TargetDto } from '../services/api'
import LoadingSpinner from './common/LoadingSpinner'
import CampaignTargetSelector from './CampaignTargetSelector'
import { createLogger } from '../utils/createLogger'

const log = createLogger('CampaignDetailsModal')

interface CampaignDetailsModalProps {
  campaignId: string
  organisation?: Organisation
  onClose: () => void
  onUpdate?: () => void
}

type TabType = 'details' | 'results'
type SelectionMethod = 'all' | 'random' | 'department' | 'manual'

const CampaignDetailsModal: React.FC<CampaignDetailsModalProps> = ({
  campaignId,
  organisation,
  onClose,
  onUpdate
}) => {
  const [campaign, setCampaign] = useState<Campaign | null>(null)
  const [loading, setLoading] = useState(true)
  const [isEditing, setIsEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [activeTab, setActiveTab] = useState<TabType>('details')
  
  // Target pool data
  const [availableTargets, setAvailableTargets] = useState<TargetDto[]>([])
  const [departments, setDepartments] = useState<string[]>([])
  const [loadingTargets, setLoadingTargets] = useState(false)
  
  // Edit form state
  const [editForm, setEditForm] = useState({
    campaign_display_name: '',
    campaign_difficulty: 'easy' as 'easy' | 'moderate' | 'hard',
    schedule_date: '',
    schedule_time: '',
    target_method: 'all' as SelectionMethod,
    target_sample_size: 0,
    selected_emails: [] as string[],
    selected_departments: [] as string[]
  })

  useEffect(() => {
    loadCampaign()
  }, [campaignId])

  useEffect(() => {
    if (organisation) {
      loadTargetPool()
    }
  }, [organisation])

  const loadTargetPool = async () => {
    if (!organisation) {
      log.warn('No organisation provided to loadTargetPool')
      return
    }
    
    const orgId = organisation.organisation_id || organisation.id
    if (!orgId) {
      log.warn('No organisation ID found', { organisation })
      return
    }
    
    log.info('Loading target pool', { orgId })
    
    try {
      setLoadingTargets(true)
      const response = await api.targetPool.list(orgId)
      
      log.info('Loaded target pool', { 
        targetCount: response.targets.length,
        departments: response.departments 
      })
      
      setAvailableTargets(response.targets)
      setDepartments(response.departments)
    } catch (error) {
      log.error('Failed to load target pool', error instanceof Error ? error : undefined, { orgId })
      setAvailableTargets([])
      setDepartments([])
    } finally {
      setLoadingTargets(false)
    }
  }

  const loadCampaign = async () => {
    try {
      setLoading(true)
      const data = await api.campaigns.get(campaignId)
      setCampaign(data)
      
      log.info('Loaded campaign', { 
        campaignId,
        status: data.status,
        targetMethod: data.target_selection_method,
        targetCount: data.target_count,
        campaignTargets: data.campaign_targets?.length,
        selectedDepartments: data.selected_departments
      })
      
      // Extract schedule data
      const startDate = data.campaign_start || data.schedule?.campaign_start || data.schedule?.start_date || ''
      
      // Determine selection method
      let targetMethod: SelectionMethod = 'all'
      let selectedEmails: string[] = []
      let selectedDepartments: string[] = []
      let sampleSize = 0
      
      // Check for department selection first (new feature)
      if (data.selected_departments && data.selected_departments.length > 0) {
        targetMethod = 'department'
        selectedDepartments = data.selected_departments
      } else if (data.target_selection_method) {
        targetMethod = data.target_selection_method as SelectionMethod
      } else if (data.targets?.method) {
        targetMethod = data.targets.method as SelectionMethod
      }
      
      // Handle manual selection
      if (targetMethod === 'manual') {
        selectedEmails = data.campaign_targets || data.targets?.emails || []
      }
      
      // Handle sample size for random selection
      if (targetMethod === 'random') {
        sampleSize = data.target_sample_size || data.targets?.sample_size || 1
      }
      
      // Handle "all" selection
      if (targetMethod === 'all') {
        sampleSize = data.target_count || availableTargets.length
      }
      
      setEditForm({
        campaign_display_name: data.campaign_display_name || '',
        campaign_difficulty: data.campaign_difficulty || 'easy',
        schedule_date: startDate ? new Date(startDate).toISOString().split('T')[0] : '',
        schedule_time: startDate ? new Date(startDate).toISOString().split('T')[1].slice(0, 5) : '',
        target_method: targetMethod,
        target_sample_size: sampleSize,
        selected_emails: selectedEmails,
        selected_departments: selectedDepartments
      })
      
      log.info('Initialized edit form', { 
        targetMethod, 
        selectedEmailsCount: selectedEmails.length,
        selectedDepartmentsCount: selectedDepartments.length,
        sampleSize 
      })
    } catch (error) {
      log.error('Failed to load campaign details', error instanceof Error ? error : undefined, { campaignId })
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    if (!campaign) return
    
    try {
      setSaving(true)
      
      const updateData: any = {
        campaign_display_name: editForm.campaign_display_name,
        campaign_difficulty: editForm.campaign_difficulty
      }
      
      // Handle schedule
      if (editForm.schedule_date) {
        const scheduledDateTime = editForm.schedule_time 
          ? `${editForm.schedule_date}T${editForm.schedule_time}:00Z`
          : `${editForm.schedule_date}T09:00:00Z`
        
        updateData.campaign_start = scheduledDateTime
        updateData.sending_window_hours = 4
        updateData.tracking_period_days = 7
      } else {
        updateData.campaign_start = null
        updateData.campaign_end = null
        updateData.tracking_end = null
      }
      
      // Handle target selection
      if (editForm.target_method === 'all') {
        // All targets - clear specific selections
        updateData.target_selection_method = 'all'
        updateData.target_sample_size = availableTargets.length
        updateData.campaign_targets = null
        updateData.selected_departments = null
        
      } else if (editForm.target_method === 'random') {
        // Random sample
        updateData.target_selection_method = 'random'
        updateData.target_sample_size = editForm.target_sample_size
        updateData.campaign_targets = null
        updateData.selected_departments = null
        
      } else if (editForm.target_method === 'department') {
        // Department selection - convert to manual with expanded emails
        const departmentEmails = availableTargets
          .filter(t => editForm.selected_departments.includes(t.department || ''))
          .map(t => t.target_email)
        
        updateData.target_selection_method = 'manual'
        updateData.selected_departments = editForm.selected_departments // Store for display
        updateData.campaign_targets = departmentEmails
        updateData.target_sample_size = departmentEmails.length
        
      } else if (editForm.target_method === 'manual') {
        // Manual selection
        updateData.target_selection_method = 'manual'
        updateData.campaign_targets = editForm.selected_emails
        updateData.target_sample_size = editForm.selected_emails.length
        updateData.selected_departments = null
      }
      
      log.info('Updating campaign', { 
        campaignId, 
        method: editForm.target_method,
        targetCount: updateData.target_sample_size,
        hasTargets: !!updateData.campaign_targets,
        hasDepartments: !!updateData.selected_departments
      })
      
      await api.campaigns.update(campaignId, updateData)
      
      await loadCampaign()
      log.info('Campaign updated successfully', { campaignId })
      setIsEditing(false)
      
      if (onUpdate) {
        onUpdate()
      }
    } catch (error) {
      log.error('Failed to update campaign', error instanceof Error ? error : undefined, { campaignId })
      alert('Failed to update campaign. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!campaign) return
    
    if (!confirm('Are you sure you want to delete this campaign? This action cannot be undone.')) {
      return
    }
    
    try {
      await api.campaigns.delete(campaignId)
      if (onUpdate) {
        onUpdate()
      }
      onClose()
    } catch (error) {
      log.error('Failed to delete campaign', error instanceof Error ? error : undefined, { campaignId })
      alert('Failed to delete campaign. Please try again.')
    }
  }

  const canEdit = campaign?.status === 'draft' || campaign?.status === 'scheduled'
  const canDelete = campaign?.status === 'draft' || campaign?.status === 'scheduled'

  // Calculate display count for read-only view
  const getDisplayTargetCount = () => {
    if (!campaign) return 0
    
    if (campaign.target_selection_method === 'department' && campaign.selected_departments) {
      // Calculate from selected departments
      return availableTargets.filter(t => 
        campaign.selected_departments?.includes(t.department || '')
      ).length
    }
    
    return campaign.target_sample_size || 
           campaign.target_count || 
           campaign.campaign_targets?.length || 
           0
  }

  // Callback handlers with proper state updates
  const handleSelectionMethodChange = (method: SelectionMethod) => {
    log.info('Selection method changed', { from: editForm.target_method, to: method })
    setEditForm(prev => {
      const newForm = {
        ...prev,
        target_method: method
      }
      log.info('New form state', { target_method: newForm.target_method })
      return newForm
    })
  }

  const handleSelectedEmailsChange = (emails: string[]) => {
    log.info('Selected emails changed', { count: emails.length })
    setEditForm(prev => ({
      ...prev,
      selected_emails: emails
    }))
  }

  const handleSelectedDepartmentsChange = (depts: string[]) => {
    log.info('Selected departments changed', { count: depts.length, depts })
    setEditForm(prev => ({
      ...prev,
      selected_departments: depts
    }))
  }

  const handleSampleSizeChange = (size: number) => {
    log.info('Sample size changed', { size })
    setEditForm(prev => ({
      ...prev,
      target_sample_size: size
    }))
  }

  const renderDetailsTab = () => {
    if (!campaign) return null
    
    return (
      <div className="space-y-6">
        {/* Campaign Name */}
        <div>
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Campaign Name</h3>
          {isEditing ? (
            <input
              type="text"
              value={editForm.campaign_display_name}
              onChange={(e) => setEditForm(prev => ({ ...prev, campaign_display_name: e.target.value }))}
              className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          ) : (
            <p className="mt-1 text-lg text-gray-900 dark:text-white">{campaign.campaign_display_name}</p>
          )}
        </div>

        {/* Status */}
        <div>
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Status</h3>
          <p className="mt-1">
            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full
              ${campaign.status === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : ''}
              ${campaign.status === 'scheduled' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' : ''}
              ${campaign.status === 'complete' ? 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300' : ''}
              ${campaign.status === 'draft' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' : ''}
            `}>
              {campaign.status}
            </span>
          </p>
        </div>

        {/* Difficulty */}
        <div>
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Difficulty</h3>
          {isEditing ? (
            <select
              value={editForm.campaign_difficulty}
              onChange={(e) => setEditForm(prev => ({ ...prev, campaign_difficulty: e.target.value as 'easy' | 'moderate' | 'hard' }))}
              className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="easy">Easy</option>
              <option value="moderate">Moderate</option>
              <option value="hard">Hard</option>
            </select>
          ) : (
            <p className="mt-1 text-gray-900 dark:text-white capitalize">{campaign.campaign_difficulty || 'easy'}</p>
          )}
        </div>

        {/* Target Selection */}
        <div>
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">Target Selection</h3>
          {isEditing ? (
            <CampaignTargetSelector
              organisationId={organisation?.organisation_id || organisation?.id || ''}
              availableTargets={availableTargets}
              departments={departments}
              loading={loadingTargets}
              selectionMethod={editForm.target_method}
              selectedEmails={editForm.selected_emails}
              selectedDepartments={editForm.selected_departments}
              sampleSize={editForm.target_sample_size}
              onSelectionMethodChange={handleSelectionMethodChange}
              onSelectedEmailsChange={handleSelectedEmailsChange}
              onSelectedDepartmentsChange={handleSelectedDepartmentsChange}
              onSampleSizeChange={handleSampleSizeChange}
            />
          ) : (
            <div>
              <p className="mt-1 text-gray-900 dark:text-white">
                {getDisplayTargetCount()} recipient{getDisplayTargetCount() !== 1 ? 's' : ''}
                <span className="text-gray-600 dark:text-gray-400">
                  {' '}(
                  {campaign.target_selection_method === 'all' ? 'all targets' : 
                   campaign.target_selection_method === 'random' ? 'random sample' :
                   campaign.target_selection_method === 'department' ? 'by department' :
                   campaign.target_selection_method === 'manual' ? 'manual selection' : 
                   campaign.target_selection_method}
                  )
                </span>
              </p>
              
              {/* Show department details */}
              {campaign.target_selection_method === 'department' && 
               campaign.selected_departments && campaign.selected_departments.length > 0 && (
                <div className="mt-2">
                  <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Selected departments:</div>
                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded p-2">
                    {campaign.selected_departments.map((dept, index) => {
                      const deptCount = availableTargets.filter(t => t.department === dept).length
                      return (
                        <div key={index} className="text-sm text-gray-700 dark:text-gray-300 py-0.5">
                          • {dept} <span className="text-gray-500 dark:text-gray-400">({deptCount})</span>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
              
              {/* Show manual selection details */}
              {campaign.target_selection_method === 'manual' && 
               campaign.campaign_targets && campaign.campaign_targets.length > 0 && (
                <div className="mt-2">
                  <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Selected targets:</div>
                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded p-2">
                    {campaign.campaign_targets.length <= 3 ? (
                      campaign.campaign_targets.map((email, index) => (
                        <div key={index} className="text-sm text-gray-700 dark:text-gray-300 py-0.5">
                          • {email}
                        </div>
                      ))
                    ) : (
                      <>
                        {campaign.campaign_targets.slice(0, 2).map((email, index) => (
                          <div key={index} className="text-sm text-gray-700 dark:text-gray-300 py-0.5">
                            • {email}
                          </div>
                        ))}
                        <details className="mt-1">
                          <summary className="text-sm text-gray-600 dark:text-gray-400 cursor-pointer hover:text-gray-800 dark:hover:text-gray-200">
                            And {campaign.campaign_targets.length - 2} more...
                          </summary>
                          <div className="mt-1 max-h-32 overflow-y-auto">
                            {campaign.campaign_targets.slice(2).map((email, index) => (
                              <div key={index + 2} className="text-sm text-gray-700 dark:text-gray-300 py-0.5">
                                • {email}
                              </div>
                            ))}
                          </div>
                        </details>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Schedule Section */}
        <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Schedule</h3>
          
          {isEditing && (campaign.status === 'draft' || campaign.status === 'scheduled') && (
            <div className="mb-6">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Date</label>
                  <input
                    type="date"
                    value={editForm.schedule_date}
                    onChange={(e) => setEditForm(prev => ({ ...prev, schedule_date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Time</label>
                  <input
                    type="time"
                    value={editForm.schedule_time}
                    onChange={(e) => setEditForm(prev => ({ ...prev, schedule_time: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                Clear both date and time to revert campaign to draft status
              </p>
            </div>
          )}

          {!isEditing && (
            <>
              {(campaign.campaign_start || campaign.schedule?.campaign_start || campaign.schedule?.start_date) ? (
                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">Start Date & Time</h4>
                    <p className="mt-1 text-gray-900 dark:text-white">
                      {new Date(campaign.campaign_start || campaign.schedule?.campaign_start || campaign.schedule?.start_date || '').toLocaleString('en-GB', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>

                  {(campaign.campaign_end || campaign.schedule?.campaign_end) && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">Sending Window Ends</h4>
                      <p className="mt-1 text-gray-900 dark:text-white">
                        {new Date(campaign.campaign_end || campaign.schedule?.campaign_end || '').toLocaleString('en-GB', {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        Emails will be sent randomly during this {campaign.sending_window_hours || 4} hour window
                      </p>
                    </div>
                  )}

                  {(campaign.tracking_end || campaign.schedule?.tracking_end) && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">Tracking Period Ends</h4>
                      <p className="mt-1 text-gray-900 dark:text-white">
                        {new Date(campaign.tracking_end || campaign.schedule?.tracking_end || '').toLocaleString('en-GB', {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        Click tracking will stop after {campaign.tracking_period_days || 7} days
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <svg className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p>Campaign not scheduled yet</p>
                  {canEdit && !isEditing && (
                    <button
                      onClick={() => setIsEditing(true)}
                      className="mt-3 text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                    >
                      Schedule Campaign
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    )
  }

  const renderResultsTab = () => {
    if (!campaign) return null
    
    const results = campaign.aggregated_results
    
    if (!results) {
      return (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          <svg className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          <p>{campaign.status === 'draft' || campaign.status === 'scheduled' ? 'No results yet - campaign has not started' : 'No results available'}</p>
        </div>
      )
    }
    
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
            <p className="text-sm text-gray-600 dark:text-gray-400">Emails Sent</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">{results.emails_sent}</p>
          </div>
          <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
            <p className="text-sm text-gray-600 dark:text-gray-400">Delivery Rate</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">{results.delivery_rate?.toFixed(1)}%</p>
          </div>
        </div>

        <div>
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">Engagement Metrics</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600 dark:text-gray-400">Opened</span>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-900 dark:text-white">{results.emails_opened}</span>
                <span className="text-sm text-gray-500 dark:text-gray-400">({results.open_rate?.toFixed(1)}%)</span>
              </div>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600 dark:text-gray-400">Clicked</span>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-900 dark:text-white">{results.links_clicked}</span>
                <span className="text-sm text-gray-500 dark:text-gray-400">({results.click_rate?.toFixed(1)}%)</span>
              </div>
            </div>
            {results.credentials_entered > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600 dark:text-gray-400">Credentials Entered</span>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-900 dark:text-white">{results.credentials_entered}</span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">({results.credential_rate?.toFixed(1)}%)</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {results.first_click && (
          <div>
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">Response Times</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">First Click</span>
                <span className="text-gray-900 dark:text-white">{new Date(results.first_click).toLocaleString()}</span>
              </div>
              {results.fastest_click_seconds && (
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Fastest Click</span>
                  <span className="text-gray-900 dark:text-white">{Math.floor(results.fastest_click_seconds / 60)} minutes</span>
                </div>
              )}
              {results.average_click_time_hours && (
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Average Click Time</span>
                  <span className="text-gray-900 dark:text-white">{results.average_click_time_hours.toFixed(1)} hours</span>
                </div>
              )}
            </div>
          </div>
        )}

        <div>
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Click Rate Performance</h3>
          <div className="relative pt-1">
            <div className="overflow-hidden h-4 text-xs flex rounded bg-gray-200 dark:bg-gray-600">
              <div 
                style={{ width: `${results.click_rate || 0}%` }}
                className={`shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center
                  ${(results.click_rate || 0) < 20 ? 'bg-green-500' : ''}
                  ${(results.click_rate || 0) >= 20 && (results.click_rate || 0) < 40 ? 'bg-yellow-500' : ''}
                  ${(results.click_rate || 0) >= 40 ? 'bg-red-500' : ''}
                `}
              />
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {(results.click_rate || 0) < 20 ? 'Excellent - Low susceptibility' : ''}
              {(results.click_rate || 0) >= 20 && (results.click_rate || 0) < 40 ? 'Moderate - Some training needed' : ''}
              {(results.click_rate || 0) >= 40 ? 'High Risk - Urgent training required' : ''}
            </p>
          </div>
        </div>
      </div>
    )
  }

  // Validation for save button
  const canSave = () => {
    if (!editForm.campaign_display_name) return false
    
    if (editForm.target_method === 'manual' && editForm.selected_emails.length === 0) {
      return false
    }
    
    if (editForm.target_method === 'department' && editForm.selected_departments.length === 0) {
      return false
    }
    
    return true
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-3xl max-h-[90vh] bg-white dark:bg-gray-800 rounded-lg shadow-xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Campaign Details</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <nav className="flex -mb-px px-6">
            <button
              onClick={() => setActiveTab('details')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'details'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              Details
            </button>
            <button
              onClick={() => setActiveTab('results')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'results'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              Results
            </button>
          </nav>
        </div>

        {/* Scrollable Content */}
        <div className="overflow-y-auto flex-1 p-6">
          {loading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner size="lg" />
            </div>
          ) : campaign ? (
            <>
              {activeTab === 'details' && renderDetailsTab()}
              {activeTab === 'results' && renderResultsTab()}
            </>
          ) : (
            <p className="text-gray-500 dark:text-gray-400">Campaign not found</p>
          )}
        </div>

        {/* Footer with Actions */}
        {campaign && activeTab === 'details' && (
          <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
            <div>
              {canDelete && !isEditing && (
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 text-sm font-medium text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                >
                  Delete Campaign
                </button>
              )}
            </div>
            <div className="flex gap-3">
              {isEditing ? (
                <>
                  <button
                    onClick={() => {
                      setIsEditing(false)
                      loadCampaign()
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white text-sm font-medium rounded transition-colors disabled:opacity-50"
                    disabled={saving || !canSave()}
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </>
              ) : (
                <>
                  {canEdit && (
                    <button
                      onClick={() => setIsEditing(true)}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white text-sm font-medium rounded transition-colors"
                    >
                      Edit Campaign
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default CampaignDetailsModal
